import { useQuery } from "@tanstack/react-query";
import { sankey, sankeyLinkHorizontal, type SankeyGraph } from "d3-sankey";
import { useEffect, useMemo, useRef, useState } from "react";

import { entities as entitiesApi, relationships as relApi } from "@/lib/api";
import type { ApiRelationship } from "@/lib/api";
import { getDomainHints } from "@/lib/domain-hints";
import { useWorkspaces } from "@/store/workspaces";

/**
 * LiveFlowCanvas — Sankey of the workspace's relationship structure.
 *
 * The flow goes: **source entity type → relationship type → target entity type**.
 * Width of each ribbon = number of relationships of that combination. This
 * makes the structural pattern of the graph visible at a glance (e.g. "all
 * armed_groups controls 3 municipalities and allies_with 1 armed_group").
 *
 * For a workspace with very heterogeneous data this view becomes the
 * conventional "system A → category → system B" Sankey by setting the
 * granularity to types. We can add an entity-level mode later if the demo
 * needs to show individual flows.
 */

const TYPE_HEX: Record<string, string> = {
  armed_group: "#e55a3c",
  municipality: "#3b82f6",
  military_base: "#22b8d4",
  infrastructure: "#f59e0b",
  individual: "#22c55e",
  political_figure: "#a855f7",
  civilian_organization: "#eab308",
  default: "#9ca3af",
};

const REL_HEX = "#64748b"; // neutral slate for relationship middle column

function colorFor(name: string, kind: "type" | "rel"): string {
  if (kind === "rel") return REL_HEX;
  return TYPE_HEX[name] ?? TYPE_HEX.default;
}

interface SankeyNode {
  id: string;
  name: string;
  kind: "src-type" | "rel-type" | "tgt-type";
  // Provided by d3-sankey after layout:
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
  value?: number;
  index?: number;
}

interface SankeyLink {
  source: number | SankeyNode;
  target: number | SankeyNode;
  value: number;
  // Provided by d3-sankey:
  y0?: number;
  y1?: number;
  width?: number;
}

interface BuildResult {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

function build(
  entityById: Record<string, { name: string; type: string }>,
  rels: ApiRelationship[],
): BuildResult {
  // Bucket: src_type+rel_type+tgt_type → count
  const counts = new Map<string, { src: string; rel: string; tgt: string; n: number }>();
  for (const r of rels) {
    const s = entityById[r.source_entity_id];
    const t = entityById[r.target_entity_id];
    if (!s || !t) continue;
    const key = `${s.type}|${r.type}|${t.type}`;
    const existing = counts.get(key);
    if (existing) existing.n += 1;
    else counts.set(key, { src: s.type, rel: r.type, tgt: t.type, n: 1 });
  }

  // Build nodes — one per (src-type), one per rel-type, one per (tgt-type)
  const nodeIdx = new Map<string, number>();
  const nodes: SankeyNode[] = [];
  const ensure = (id: string, name: string, kind: SankeyNode["kind"]): number => {
    const existing = nodeIdx.get(id);
    if (existing !== undefined) return existing;
    const idx = nodes.length;
    nodes.push({ id, name, kind });
    nodeIdx.set(id, idx);
    return idx;
  };

  const links: SankeyLink[] = [];
  for (const { src, rel, tgt, n } of counts.values()) {
    const sIdx = ensure(`src:${src}`, src, "src-type");
    const rIdx = ensure(`rel:${rel}`, rel, "rel-type");
    const tIdx = ensure(`tgt:${tgt}`, tgt, "tgt-type");
    links.push({ source: sIdx, target: rIdx, value: n });
    links.push({ source: rIdx, target: tIdx, value: n });
  }
  return { nodes, links };
}

export function LiveFlowCanvas({ workspaceId }: { workspaceId: string }) {
  const ents = useQuery({
    queryKey: ["entities", workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const rels = useQuery({
    queryKey: ["relationships", workspaceId],
    queryFn: () => relApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const entityById = useMemo(
    () =>
      Object.fromEntries(
        (ents.data ?? []).map((e) => [e.id, { name: e.name, type: e.type }]),
      ),
    [ents.data],
  );
  const graph = useMemo(() => build(entityById, rels.data ?? []), [entityById, rels.data]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 800, h: 500 });
  const [hover, setHover] = useState<{ kind: "node" | "link"; idx: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(200, width), h: Math.max(200, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute layout with d3-sankey
  const laid = useMemo(() => {
    if (graph.nodes.length === 0) return null;
    const layout = sankey<SankeyNode, SankeyLink>()
      .nodeId((d) => String(d.index))
      .nodeWidth(14)
      .nodePadding(18)
      .extent([
        [40, 24],
        [size.w - 40, size.h - 30],
      ]);
    const g: SankeyGraph<SankeyNode, SankeyLink> = {
      nodes: graph.nodes.map((n) => ({ ...n })),
      links: graph.links.map((l) => ({ ...l })),
    };
    return layout(g);
  }, [graph, size.w, size.h]);

  const totalRels = rels.data?.length ?? 0;

  return (
    <div className="relative h-full w-full overflow-hidden rounded-md bg-[oklch(0.14_0.01_260)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface/60 text-[11px]">
        <div className="flex items-center gap-4 text-foreground/80">
          <span>
            <span className="font-mono text-foreground">{totalRels}</span>{" "}
            <span className="text-muted-foreground">relationships</span>
          </span>
          <span className="text-muted-foreground">
            source type → relationship → target type · width = count
          </span>
        </div>
      </div>

      <div ref={containerRef} className="relative flex-1 min-h-0">
        {totalRels === 0 ? (
          <Empty workspaceId={workspaceId} />
        ) : laid ? (
          <svg width={size.w} height={size.h} className="absolute inset-0">
            {/* Links */}
            <g>
              {laid.links.map((l, i) => {
                const path = sankeyLinkHorizontal()(l as never) ?? "";
                const srcNode = l.source as SankeyNode;
                const c = colorFor(srcNode.name, srcNode.kind === "rel-type" ? "rel" : "type");
                const isHover = hover?.kind === "link" && hover.idx === i;
                return (
                  <path
                    key={i}
                    d={path}
                    fill="none"
                    stroke={c}
                    strokeOpacity={isHover ? 0.85 : 0.45}
                    strokeWidth={Math.max(1, (l.width ?? 1))}
                    onMouseEnter={() => setHover({ kind: "link", idx: i })}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: "pointer" }}
                  />
                );
              })}
            </g>
            {/* Nodes */}
            <g>
              {laid.nodes.map((n, i) => {
                const c = colorFor(n.name, n.kind === "rel-type" ? "rel" : "type");
                const x = n.x0 ?? 0;
                const y = n.y0 ?? 0;
                const w = (n.x1 ?? 0) - (n.x0 ?? 0);
                const h = (n.y1 ?? 0) - (n.y0 ?? 0);
                const isLeft = n.kind === "src-type";
                const isRight = n.kind === "tgt-type";
                return (
                  <g key={i}>
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={Math.max(2, h)}
                      fill={c}
                      stroke="#0a0a0c"
                      strokeWidth={1}
                      onMouseEnter={() => setHover({ kind: "node", idx: i })}
                      onMouseLeave={() => setHover(null)}
                      style={{ cursor: "pointer" }}
                    />
                    <text
                      x={isLeft ? x - 6 : isRight ? x + w + 6 : x + w / 2}
                      y={y + h / 2}
                      textAnchor={isLeft ? "end" : isRight ? "start" : "middle"}
                      dominantBaseline="middle"
                      fontSize="11"
                      fontWeight={n.kind === "rel-type" ? 500 : 600}
                      fill="oklch(0.92 0.005 260)"
                      pointerEvents="none"
                    >
                      {n.name}
                      <tspan
                        dx={isLeft ? -6 : isRight ? 6 : 0}
                        fill="oklch(0.60 0.015 260)"
                        fontSize="10"
                      >
                        {n.value}
                      </tspan>
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        ) : null}

        {/* Hover detail */}
        {hover && laid && (
          <HoverDetail
            laid={laid}
            kind={hover.kind}
            idx={hover.idx}
          />
        )}
      </div>
    </div>
  );
}

function HoverDetail({
  laid,
  kind,
  idx,
}: {
  laid: SankeyGraph<SankeyNode, SankeyLink>;
  kind: "node" | "link";
  idx: number;
}) {
  if (kind === "node") {
    const n = laid.nodes[idx];
    return (
      <div className="absolute left-3 top-3 z-20 rounded-md border border-border bg-popover px-3 py-2 text-[11px] shadow-xl">
        <div className="font-semibold text-foreground">{n.name}</div>
        <div className="text-muted-foreground mt-0.5">
          {n.kind === "src-type" && "source type"}
          {n.kind === "rel-type" && "relationship type"}
          {n.kind === "tgt-type" && "target type"}
          {" · "}
          <span className="text-foreground/85">{n.value} flow{n.value === 1 ? "" : "s"}</span>
        </div>
      </div>
    );
  }
  const l = laid.links[idx];
  const s = l.source as SankeyNode;
  const t = l.target as SankeyNode;
  return (
    <div className="absolute left-3 top-3 z-20 rounded-md border border-border bg-popover px-3 py-2 text-[11px] shadow-xl">
      <div className="font-mono text-foreground">
        {s.name} → {t.name}
      </div>
      <div className="text-muted-foreground mt-0.5">{l.value} relationship{l.value === 1 ? "" : "s"}</div>
    </div>
  );
}

function Empty({ workspaceId }: { workspaceId?: string }) {
  const { getById } = useWorkspaces();
  const ws = workspaceId ? getById(workspaceId) : undefined;
  const example = getDomainHints(ws?.domain).flowExample;
  return (
    <div className="absolute inset-0 grid place-items-center text-center px-6">
      <div className="max-w-sm">
        <div className="label-eyebrow mb-2">No relationships yet</div>
        <h3 className="text-[15px] font-semibold mb-2">Empty flow</h3>
        <p className="text-[12px] text-muted-foreground">
          Add relationships via chat. Try:{" "}
          <span className="text-foreground/85 italic">{example}</span>
        </p>
      </div>
    </div>
  );
}
